import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { TimeTrackingProvider } from './contexts/TimeTrackingContext';

// Lazy load all route components for code splitting
// This reduces initial bundle size by loading components only when needed
const LandingPage = lazy(() => import('./components/LandingPage'));
const FreeTrialApp = lazy(() => import('./components/FreeTrialApp'));
const Pricing = lazy(() => import('./components/Pricing'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentError = lazy(() => import('./components/PaymentError'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));
const NotFound = lazy(() => import('./components/NotFound'));

// Loading spinner component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

const AppRouter = () => {
  const { isAuthenticated, isLoading, user } = useAuth0();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Public pricing page */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Legal pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />

        {/* Payment success page */}
        <Route path="/payment/success" element={<PaymentSuccess />} />

        {/* Payment error page */}
        <Route path="/payment/error" element={<PaymentError />} />

        {/* Protected main app - Free Trial (handles /app, /app/calendar, /app/membership, etc.) */}
        <Route
          path="/app/*"
          element={
            isAuthenticated ? (
              <TimeTrackingProvider userId={user?.sub}>
                <FreeTrialApp />
              </TimeTrackingProvider>
            ) : <Navigate to="/" replace />
          }
        />

        {/* 404 Not Found - catch all unknown routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
